import { useEffect, useMemo, useRef, useState } from "react";
import { CandidateCard } from "./CandidateCard";
import { api } from "../lib/api";
import type { Candidate, Position } from "../types";

interface Props {
  positions: Position[];
  archivedMode?: boolean;
}

type RecFilter = "all" | "strong_hire" | "consider" | "reject";
type ScoreRange = "all" | "90+" | "80-89" | "60-79" | "<60";
type StarFilter = "all" | "5" | "4" | "3" | "2" | "1";
type SortBy = "score_desc" | "score_asc" | "name_asc" | "date_desc";

export function CandidatesPage({ positions, archivedMode }: Props) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterPositionId, setFilterPositionId] = useState<number | null>(null);
  const [filterRec, setFilterRec] = useState<RecFilter>("all");
  const [filterScore, setFilterScore] = useState<ScoreRange>("all");
  const [filterStars, setFilterStars] = useState<StarFilter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");

  const refresh = async () => {
    setLoading(true);
    try {
      const { candidates } = await api.listCandidates({
        positionId: filterPositionId ?? undefined,
        archived: archivedMode,
      });
      setCandidates(candidates);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, [filterPositionId, archivedMode]);

  const handleArchive = async (id: number, archived: boolean) => {
    await api.archiveCandidate(id, archived);
    setCandidates((prev) => prev.filter((c) => c.id !== id));
  };

  const [deleteTarget, setDeleteTarget] = useState<Candidate | null>(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await api.deleteCandidate(deleteTarget.id);
    setCandidates((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const filtered = useMemo(() => {
    let list = candidates;

    // Search
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (c) =>
          (c.name ?? "").toLowerCase().includes(q) ||
          (c.email ?? "").toLowerCase().includes(q) ||
          (c.filename ?? "").toLowerCase().includes(q) ||
          (c.summary ?? "").toLowerCase().includes(q) ||
          c.skills.some((s) => s.toLowerCase().includes(q))
      );
    }

    // Recommendation
    if (filterRec !== "all") {
      list = list.filter((c) => c.recommendation === filterRec);
    }

    // Score range
    if (filterScore !== "all") {
      list = list.filter((c) => {
        const s = c.match_score;
        switch (filterScore) {
          case "90+": return s >= 90;
          case "80-89": return s >= 80 && s < 90;
          case "60-79": return s >= 60 && s < 80;
          case "<60": return s < 60;
        }
      });
    }

    // Star rating
    if (filterStars !== "all") {
      const minStars = Number(filterStars);
      list = list.filter((c) => {
        const stars = Math.round((c.match_score / 100) * 5 * 2) / 2;
        return stars >= minStars && stars < minStars + 1;
      });
    }

    // Sort
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "score_desc":
          return b.match_score - a.match_score;
        case "score_asc":
          return a.match_score - b.match_score;
        case "name_asc":
          return (a.name ?? "").localeCompare(b.name ?? "");
        case "date_desc":
          return b.created_at.localeCompare(a.created_at);
      }
    });

    return list;
  }, [candidates, search, filterRec, filterScore, filterStars, sortBy]);

  const counts = useMemo(
    () => ({
      all: candidates.length,
      strong_hire: candidates.filter((c) => c.recommendation === "strong_hire").length,
      consider: candidates.filter((c) => c.recommendation === "consider").length,
      reject: candidates.filter((c) => c.recommendation === "reject").length,
    }),
    [candidates]
  );

  const activeFilterCount =
    (search !== "" ? 1 : 0) +
    (filterRec !== "all" ? 1 : 0) +
    (filterScore !== "all" ? 1 : 0) +
    (filterStars !== "all" ? 1 : 0) +
    (filterPositionId !== null ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    setSearch("");
    setFilterPositionId(null);
    setFilterRec("all");
    setFilterScore("all");
    setFilterStars("all");
  };

  return (
    <div className="space-y-6">
      {/* Header + search + filter toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
            {archivedMode ? "Archived" : "All candidates"}
          </h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {candidates.length}
          </span>
        </div>
        <div className="flex flex-1 items-center gap-2 sm:max-w-md">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
            <input
              className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={[
              "flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors",
              filtersOpen || hasActiveFilters
                ? "bg-near-black text-white"
                : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
            ].join(" ")}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
              />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {filtersOpen && (
        <div className="rounded-card bg-white p-6 shadow-card">
          {/* Filter row */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Position
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={filterPositionId ?? ""}
                onChange={(e) =>
                  setFilterPositionId(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                <option value="">All positions</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Score range
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={filterScore}
                onChange={(e) => setFilterScore(e.target.value as ScoreRange)}
              >
                <option value="all">All scores</option>
                <option value="90+">90+ (Excellent)</option>
                <option value="80-89">80–89 (Strong)</option>
                <option value="60-79">60–79 (Consider)</option>
                <option value="<60">Below 60 (Reject)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Sort by
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
              >
                <option value="score_desc">Score (high → low)</option>
                <option value="score_asc">Score (low → high)</option>
                <option value="name_asc">Name (A → Z)</option>
                <option value="date_desc">Newest first</option>
              </select>
            </div>

            {/* Star rating filter */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Star rating
              </label>
              <div className="mt-1 flex gap-1">
                {(
                  [
                    ["all", "All"],
                    ["5", "5"],
                    ["4", "4"],
                    ["3", "3"],
                    ["2", "2"],
                    ["1", "1"],
                  ] as [StarFilter, string][]
                ).map(([value, label]) => {
                  const starColor =
                    value === "all" ? "" :
                    Number(value) >= 4 ? "text-yellow-400" :
                    Number(value) >= 2 ? "text-orange-400" : "text-[#ff0000]";
                  return (
                    <button
                      key={value}
                      onClick={() => setFilterStars(value)}
                      className={[
                        "flex items-center gap-0.5 rounded-btn px-2 py-1.5 text-xs font-medium transition-colors",
                        filterStars === value
                          ? "bg-near-black text-white"
                          : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
                      ].join(" ")}
                    >
                      {value !== "all" && (
                        <svg className={`h-3 w-3 ${filterStars === value ? "text-white" : starColor}`} viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      )}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recommendation pills */}
          <div className="mt-3">
            <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Recommendation
            </label>
            <div className="mt-1 flex flex-wrap gap-1">
              {(
                [
                  ["all", "All"],
                  ["strong_hire", "Strong hire"],
                  ["consider", "Consider"],
                  ["reject", "Reject"],
                ] as [RecFilter, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setFilterRec(value)}
                  className={[
                    "rounded-btn px-3 py-1.5 text-xs font-medium transition-colors",
                    filterRec === value
                      ? value === "strong_hire"
                        ? "bg-emerald-100 text-emerald-800"
                        : value === "consider"
                          ? "bg-amber-100 text-amber-800"
                          : value === "reject"
                            ? "bg-rose-100 text-error-text"
                            : "bg-near-black text-white"
                      : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
                  ].join(" ")}
                >
                  {label}{" "}
                  <span className="tabular-nums opacity-70">
                    ({counts[value]})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-text-secondary">
                Showing {filtered.length} of {candidates.length} candidates
              </p>
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-rausch hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <p className="text-text-secondary">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
          {candidates.length === 0
            ? archivedMode
              ? "No archived candidates."
              : "No candidates have been screened yet."
            : "No candidates match the current filters."}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((c) => (
            <CandidateCard
              key={c.id}
              candidate={c}
              onArchive={handleArchive}
              onDelete={archivedMode ? (id) => {
                const target = candidates.find((x) => x.id === id);
                if (target) setDeleteTarget(target);
              } : undefined}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <DeleteConfirmDialog
          candidateName={deleteTarget.name || "this candidate"}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function DeleteConfirmDialog({
  candidateName,
  onConfirm,
  onCancel,
}: {
  candidateName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel]);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === backdropRef.current) onCancel();
      }}
    >
      <div className="mx-4 w-full max-w-md rounded-card bg-white p-6 shadow-card">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100">
            <svg className="h-5 w-5 text-error-text" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[18px] font-semibold text-near-black">
              Delete candidate
            </h3>
            <p className="mt-0.5 text-sm text-text-secondary">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm text-text-focused">
          Are you sure you want to permanently delete{" "}
          <span className="font-semibold text-near-black">{candidateName}</span>?
          All screening data, scores, and breakdown will be lost.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-btn bg-surface-secondary px-5 py-2 text-sm font-medium text-text-focused transition-colors hover:bg-border-card"
          >
            Cancel
          </button>
          <button
            disabled={deleting}
            onClick={handleConfirm}
            className="rounded-btn bg-red-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:bg-border-card disabled:text-text-secondary"
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
